import {
  SCHEDULER_SCRIPT_ID,
  VersionManifest,
} from '@wrkbelt/shared/utils-booking-flow-data-logic';
import {
  SchedulerCdnAssetPaths,
  SchedulerCdnBundleEvents,
  SchedulerListenerEvents,
} from './constants';
import { prepSchedulerForLoading } from './loader';

/**
 * Entry point for the Wrkbelt Scheduler CDN
 * This is the lightweight entry script loaded by the client website
 * It handles initial setup, version manifest fetching, and main script loading
 */
(function () {
  prepSchedulerForLoading();

  // Try to find by ID first
  let scriptElement = document.getElementById(
    SCHEDULER_SCRIPT_ID
  ) as HTMLScriptElement;

  if (!scriptElement) {
    // If not found, try to find by src pattern
    scriptElement = Array.from(document.getElementsByTagName('script')).find(
      (script) => script.src.includes('scheduler.js')
    ) as HTMLScriptElement;
  }

  if (!scriptElement) {
    console.error(
      'Wrkbelt Scheduler: Script element not found with any method'
    );
    return;
  }

  // Dispatch a custom event to the document
  const dispatchEvent = (
    eventName: SchedulerCdnBundleEvents | SchedulerListenerEvents,
    detail: Record<string, unknown> = {}
  ) => {
    try {
      document.dispatchEvent(new CustomEvent(eventName, { detail }));
    } catch (err) {
      console.error('Wrkbelt Scheduler: Failed to dispatch event', err);
    }
  };

  // Function to get the base URL from the current script
  const getBaseUrl = (): string => {
    const scriptSrc = scriptElement.src;
    const url = new URL(scriptSrc);
    return url.origin;
  };

  // Function to fetch the version manifest
  const fetchVersionManifest = async (): Promise<VersionManifest> => {
    dispatchEvent(SchedulerCdnBundleEvents.BUNDLE_LOADING);

    try {
      const baseUrl = getBaseUrl();
      // Add timestamp to prevent caching of the version manifest
      // This ensures we always get the latest version information
      const manifestUrl = `${baseUrl}${
        SchedulerCdnAssetPaths.VERSION_MANIFEST
      }?_=${new Date().getTime()}`;

      console.log(
        `Wrkbelt Scheduler: Fetching version manifest from ${manifestUrl}`
      );

      const response = await fetch(manifestUrl, {
        cache: 'no-cache',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch version manifest: ${response.status}`);
      }

      const manifest = await response.json();
      console.log('Wrkbelt Scheduler: Version manifest loaded', {
        version: manifest.version,
        commit: manifest.commit.substring(0, 7),
        entryPoint: manifest.entryPoint,
      });
      return manifest;
    } catch (error) {
      console.error(
        'Wrkbelt Scheduler: Failed to load version manifest',
        error
      );
      dispatchEvent(SchedulerCdnBundleEvents.BUNDLE_ERROR, {
        error: 'Failed to load version manifest',
      });
      throw error;
    }
  };

  // Initialize the Scheduler
  const initScheduler = async (): Promise<void> => {
    try {
      // Fetch the version manifest - this contains information about the current deployment
      // including commit hash, entry point path, and all required assets
      const manifest = await fetchVersionManifest();
      dispatchEvent(SchedulerCdnBundleEvents.BUNDLE_LOADED, { manifest });

      // Load the main script which contains the actual scheduler initialization
      // This will be the commit-versioned scheduler-core chunk
      const baseUrl = getBaseUrl();
      // Cache-bust the chunk URL using buildTime from the manifest.
      // Each build produces a unique buildTime (even same commit), so the browser
      // always fetches fresh. CloudFront's longCachePolicy strips query params
      // (queryStringBehavior: 'none'), so CDN edge caching is unaffected.
      let cacheBuster = manifest.buildTime;
      if (!cacheBuster) {
        console.warn(
          'Wrkbelt Scheduler: buildTime missing from manifest, using runtime fallback'
        );
        cacheBuster = String(Date.now());
      }
      const coreScriptUrl = `${baseUrl}${
        manifest.entryPoint
      }?v=${encodeURIComponent(cacheBuster)}`;
      console.log(
        `Wrkbelt Scheduler: Loading scheduler core from ${coreScriptUrl}`
      );

      // Create and load the script element
      const script = document.createElement('script');
      // Using the entryPoint from the manifest - this points to the versioned scheduler-core chunk
      // The entryPoint in the manifest will include the commit hash directory, e.g., /{commit-hash}/js/scheduler-core.{hash}.js
      script.src = coreScriptUrl;
      script.type = 'module';
      script.async = true;
      script.crossOrigin = 'anonymous'; // Improve error reporting

      // Create promise-based script loading with timeout
      const scriptLoadPromise = new Promise<void>((resolve, reject) => {
        // Set a timeout to detect script loading failures
        const timeout = setTimeout(() => {
          reject(
            new Error('Scheduler script loading timed out after 30 seconds')
          );
        }, 30000);

        script.onload = () => {
          clearTimeout(timeout);
          resolve();
        };

        script.onerror = (err) => {
          clearTimeout(timeout);
          reject(new Error(`Failed to load scheduler core: ${err}`));
        };
      });

      // Append script to document
      document.head.appendChild(script);

      // Wait for script to load
      await scriptLoadPromise;
      console.log('Wrkbelt Scheduler: Core script loaded successfully');

      // Wait for WrkbeltLoader to be fully initialized using a polling mechanism
      // This is more reliable than event-based communication
      const waitForLoader = async (
        maxAttempts = 50,
        intervalMs = 100
      ): Promise<void> => {
        return new Promise((resolve, reject) => {
          let attempts = 0;

          const checkLoader = () => {
            attempts++;

            // Check if WrkbeltLoader is available
            if (
              window.WrkbeltLoader &&
              typeof window.WrkbeltLoader.initScheduler === 'function'
            ) {
              console.log(
                `Wrkbelt Scheduler: Loader detected after ${attempts} attempts`
              );
              resolve();
              return;
            }

            // Check if we've exceeded max attempts
            if (attempts >= maxAttempts) {
              reject(
                new Error(
                  `Loader initialization timed out after ${maxAttempts} attempts`
                )
              );
              return;
            }

            // Try again after interval
            setTimeout(checkLoader, intervalMs);
          };

          // Start checking
          checkLoader();
        });
      };

      // Wait for the loader to be fully available
      await waitForLoader();

      // Once the loader is available, initialize the scheduler
      try {
        console.log('Wrkbelt Scheduler: Initializing with loader');
        window.WrkbeltLoader.initScheduler(scriptElement, manifest);
      } catch (initError) {
        console.error(
          'Wrkbelt Scheduler: Failed to initialize with loader',
          initError
        );
        throw initError;
      }
    } catch (error) {
      console.error('Wrkbelt Scheduler: Initialization failed', error);

      // Dispatch error event that clients can listen for
      dispatchEvent(SchedulerCdnBundleEvents.BUNDLE_ERROR, {
        error:
          error instanceof Error
            ? error.message
            : 'Unknown error during initialization',
      });
    }
  };

  // Initialize when DOM is ready - ensuring cross-browser compatibility
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initScheduler().catch((error) => {
        console.error(
          'Wrkbelt Scheduler: Unhandled initialization error',
          error
        );
      });
    });
  } else {
    initScheduler().catch((error) => {
      console.error('Wrkbelt Scheduler: Unhandled initialization error', error);
    });
  }
})();
